import { useCallback, useState } from "react";
import { ClassificationStatus } from "../utils";

// Hook personalizado para gestionar el estado de las clasificaciones
export const useClassificationsStateHook = (initialData = []) => {
	const [data, setData] = useState(initialData);
	const [pendingOperations, setPendingOperations] = useState([]);
	const [selectedItems, setSelectedItems] = useState({});

	// Función para añadir una operación pendiente
	const addPendingOperation = useCallback((type, itemIds, extraData = {}) => {
		setPendingOperations((prev) => [
			...prev,
			{
				type,
				ids: Array.isArray(itemIds) ? itemIds : [itemIds],
				data: extraData,
				timestamp: Date.now(),
			},
		]);
	}, []);

	// Función para actualizar los datos con una nueva estructura
	const updateData = useCallback((newData) => {
		setData(newData);
	}, []);

	// Función para limpiar operaciones pendientes
	const clearPendingOperations = useCallback(() => {
		setPendingOperations([]);
	}, []);

	// Toggle para selección de items
	const toggleSelection = useCallback((itemId) => {
		setSelectedItems((prev) => ({
			...prev,
			[itemId]: !prev[itemId],
		}));
	}, []);

	// Seleccionar con hijos
	const handleSelectWithChildren = useCallback(
		(node, isSelected) => {
			const collectIds = (item) => {
				let ids = [item.id];
				if (item.children && item.children.length > 0) {
					item.children.forEach((child) => {
						ids = [...ids, ...collectIds(child)];
					});
				}
				return ids;
			};

			const allIds = collectIds(node);
			const newSelection = { ...selectedItems };

			allIds.forEach((id) => {
				newSelection[id] = isSelected;
			});

			setSelectedItems(newSelection);
		},
		[selectedItems]
	);

	// Obtener los IDs seleccionados
	const getSelectedIds = useCallback(() => {
		return (
			Object.entries(selectedItems)
				// eslint-disable-next-line no-unused-vars
				.filter(([_, isSelected]) => isSelected)
				.map(([id]) => id)
		);
	}, [selectedItems]);

	const handleClassificationEdits = (editData) => {
		const { type, ids, updateData, updatedCategories, extra = { } } = editData;
		const { newChild : { id: newChildId = null, ...restNewRegister } = {} } = extra;

		if (
			type === "UPDATE_NEW" &&
			ids &&
			ids.length > 0 &&
			updatedCategories
		) {
			
			const updatedOperations = pendingOperations.map((operation) => {
				if (operation.type === "CREATE" && ids.includes(operation.ids[0])
				) {
					const findNodeById = (nodes, id) => {
						for (const node of nodes) {
							if (node.id === id) return node;
							if (node.children?.length) {
								const found = findNodeById(node.children, id);
								if (found) return found;
							}
						}
						return null;
					};

					const updatedNode = findNodeById(updatedCategories, operation.ids[0]);

					if (updatedNode) {
						return {
							...operation,
							data: updatedNode,
						};
					}
				}
				return operation;
			});

			// Actualizamos el estado con las operaciones actualizadas
			const finalUpdatedOperations = [...updatedOperations];
						
			if(newChildId !== null){
				finalUpdatedOperations.push({
					type: "CREATE",
					ids: [newChildId],
					data: {
						...restNewRegister,
						id: newChildId,
					},
					timestamp: Date.now(),
				});
			}			
			
			setPendingOperations(finalUpdatedOperations);
		} else if (type === "UPDATE" && ids && ids.length > 0) {			
			addPendingOperation("UPDATE", ids, updateData);

			if(newChildId === null) return;
			
			addPendingOperation("CREATE", [newChildId], { 
				...restNewRegister, 
				id: newChildId,
			});
		}
	};

	const handleClassificationOrder = (
		updatedData,
		blockUpdateOrder = false,
		changedItems = []
	) => {
		updateData(updatedData);
	
		if (blockUpdateOrder) return;
	
		if (changedItems && changedItems.length > 0) {
			// Separar los elementos por tipo de operación (NEW vs EDITED)
			const newItems = [];
			const editedItems = {};
	
			changedItems.forEach(item => {
				if (item.status === ClassificationStatus.NEW) {
					newItems.push(item.id);
				} else {
					// Solo guardamos los campos relevantes para la actualización de orden
					editedItems[item.id] = {
						sort_order: item.sort_order,
						status: item.status
					};
				}
			});
	
			// Actualizar operaciones CREATE existentes para elementos nuevos
			if (newItems.length > 0) {
				// Primero, creamos una copia de todas las operaciones pendientes
				const allUpdatedOperations = [...pendingOperations];
				
				// Luego, actualizamos solo las operaciones relevantes
				newItems.forEach(itemId => {
					const opIndex = allUpdatedOperations.findIndex(
						op => op.type === "CREATE" && op.ids[0] === itemId
					);
					
					if (opIndex >= 0) {
						// Buscar el nodo completo actualizado
						const findNodeById = (nodes, id) => {
							for (const node of nodes) {
								if (node.id === id) return node;
								if (node.children?.length) {
									const found = findNodeById(node.children, id);
									if (found) return found;
								}
							}
							return null;
						};
	
						const fullNode = findNodeById(updatedData, itemId);
						if (fullNode) {
							allUpdatedOperations[opIndex] = {
								...allUpdatedOperations[opIndex],
								data: fullNode,
							};
						}
					}
				});
				
				// Actualizamos todas las operaciones pendientes
				setPendingOperations(allUpdatedOperations);
			}
	
			// Gestionar operaciones UPDATE_ORDER para elementos editados
			if (Object.keys(editedItems).length > 0) {
				// Hacemos una copia de todas las operaciones pendientes actuales
				const currentOps = [...pendingOperations];
				
				// Verificar si ya existe una operación UPDATE_ORDER
				const existingOrderOpIndex = currentOps.findIndex(
					op => op.type === "UPDATE_ORDER"
				);
	
				if (existingOrderOpIndex >= 0) {
					// Obtenemos la operación existente
					const existingOp = currentOps[existingOrderOpIndex];
					
					// Combinar los IDs de elementos afectados sin duplicar
					const combinedIds = [...new Set([
						...(existingOp.ids || []), 
						...Object.keys(editedItems)
					])];
					
					// Preparamos el objeto combinado de elementos cambiados
					const existingChangedItems = existingOp.data?.changedItems || {};
					
					// Combinar los datos de los elementos, preservando datos existentes
					const combinedData = {};
					
					// Primero añadimos todos los elementos existentes
					Object.keys(existingChangedItems).forEach(id => {
						combinedData[id] = existingChangedItems[id];
					});
					
					// Luego añadimos o actualizamos con los nuevos elementos
					Object.keys(editedItems).forEach(id => {						
						if (combinedData[id]) {							
							combinedData[id] = {								
								...combinedData[id],								
								sort_order: editedItems[id].sort_order !== undefined 
									? editedItems[id].sort_order 
									: combinedData[id].sort_order,
								status: editedItems[id].status !== undefined 
									? editedItems[id].status 
									: combinedData[id].status
							};
						} else {							
							combinedData[id] = { ...editedItems[id] };
						}
					});
										
					currentOps[existingOrderOpIndex] = {
						...existingOp,
						ids: combinedIds,
						data: { changedItems: combinedData },
						timestamp: Date.now(),
					};
										
					setPendingOperations(currentOps);
				} else {
					addPendingOperation(
						"UPDATE_ORDER", 
						Object.keys(editedItems), 
						{ changedItems: editedItems }
					);
				}
			}
		} else {			
			const currentOps = [...pendingOperations];
						
			const existingOrderOpIndex = currentOps.findIndex(
				op => op.type === "UPDATE_ORDER"
			);
	
			if (existingOrderOpIndex >= 0) {
				// Actualizar la operación existente con los datos nuevos
				currentOps[existingOrderOpIndex] = {
					...currentOps[existingOrderOpIndex],
					data: { updatedData },
					timestamp: Date.now()
				};
				
				// Actualizamos todas las operaciones pendientes
				setPendingOperations(currentOps);
			} else {
				// Crear una nueva operación
				addPendingOperation("UPDATE_ORDER", [], { updatedData });
			}
		}
	};

	return {
		data,
		updateData,
		pendingOperations,
		addPendingOperation,
		clearPendingOperations,
		selectedItems,
		toggleSelection,
		handleSelectWithChildren,
		handleClassificationEdits,
		handleClassificationOrder,
		getSelectedIds,
		setSelectedItems,
	};
};
